import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bookmark, Check, MapPin, MessageSquare, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Avatar } from './Avatar';
import { OnlineDot } from './Presence';
import { useAuth } from '../auth/AuthContext';
import { useI18n } from '../i18n';
import { chatApi } from '../lib/chat';

export const ContactActions = ({ userId, initialSaved, onSaved, onNavigate, prefix = 'contact' }) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [saved, setSaved] = useState(initialSaved);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    if (initialSaved !== undefined) { setSaved(initialSaved); return; }
    chatApi(`/contacts/${encodeURIComponent(userId)}`).then((d) => { if (active) setSaved(d.saved); }).catch(() => {});
    return () => { active = false; };
  }, [userId, initialSaved]);
  if (!user || user.user_id === userId) return null;
  const toggle = async () => {
    setBusy(true);
    try {
      const data = await chatApi(`/saved/${encodeURIComponent(userId)}`, { method: saved ? 'DELETE' : 'PUT' });
      setSaved(data.saved); onSaved?.(data.saved);
      window.dispatchEvent(new Event('stagecrew:saved-contacts'));
      toast.success(t(data.saved ? 'chat.savedSuccess' : 'chat.unsaved'));
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return <div className="flex flex-wrap gap-2" data-testid={`${prefix}-actions`}>
    <Button asChild data-testid={`${prefix}-message`}><Link to={`/mensajes?usuario=${encodeURIComponent(userId)}`} onClick={onNavigate}><MessageSquare size={16} />{t('chat.send')}</Link></Button>
    <Button variant="outline" data-testid={`${prefix}-save`} disabled={busy || saved === undefined} onClick={toggle} aria-pressed={!!saved}>{saved ? <Check size={16} /> : <Bookmark size={16} />}{t(saved ? 'chat.unsave' : 'chat.save')}</Button>
  </div>;
};

export const MemberCard = ({ userId, onClose }) => {
  const { t } = useI18n();
  const [contact, setContact] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!userId) return;
    let active = true; setError('');
    chatApi(`/contacts/${encodeURIComponent(userId)}`).then((d) => { if (active) setContact(d); }).catch((err) => { if (active) setError(err.message); });
    return () => { active = false; };
  }, [userId, attempt]);
  return <Dialog open={!!userId} onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent data-testid="member-card" className="w-[calc(100%-2rem)] max-w-md max-h-[85dvh] overflow-y-auto bg-[#121418] rounded-xl text-left">
      <DialogTitle data-testid="member-card-title" className="text-amber-500 text-sm font-mono uppercase tracking-widest">{t('chat.card')}</DialogTitle>
      <DialogDescription className="sr-only">{t('chat.send')} · {t('chat.save')}</DialogDescription>
      {error ? <div role="alert" data-testid="member-card-error" className="text-sm text-red-300">{error}<Button variant="outline" className="mt-3 block" data-testid="member-card-retry" onClick={() => setAttempt((n) => n + 1)}>{t('chat.retry')}</Button></div> : !contact ? <p data-testid="member-card-loading" className="text-sm text-slate-400">{t('chat.loading')}</p> : <>
        <div className="flex gap-4 items-center min-w-0 pt-3">
          <Avatar src={contact.avatar_url} name={contact.name} size={64} />
          <div className="min-w-0"><h2 data-testid="member-card-name" className="text-lg font-semibold text-white break-words [overflow-wrap:anywhere]">{contact.name} <OnlineDot userId={contact.user_id} /></h2>
            <p data-testid="member-card-role" className="text-sm text-slate-400 break-words">{contact.is_admin ? t('chat.admin') : contact.role || t(contact.account_type === 'company' ? 'chat.company' : 'chat.member')}</p>
            {contact.handle && <p data-testid="member-card-handle" className="text-xs text-amber-500 font-mono break-all">@{contact.handle}</p>}
          </div>
        </div>
        {contact.verified && <span data-testid="member-card-verified" className="flex gap-2 text-emerald-400 text-xs"><ShieldCheck size={14} />{t('badge.verified')}</span>}
        {contact.location && <p data-testid="member-card-location" className="flex items-start gap-2 text-slate-400 text-sm break-all"><MapPin size={14} className="shrink-0 mt-1" />{contact.location}</p>}
        {contact.bio && <p data-testid="member-card-bio" className="text-sm text-slate-300 leading-relaxed [overflow-wrap:anywhere] line-clamp-5">{contact.bio}</p>}
        {!!contact.skills.length && <div data-testid="member-card-skills" className="flex flex-wrap gap-2">{contact.skills.slice(0, 6).map((s) => <span key={s} className="border border-[#2A2F3D] rounded px-2 py-1 text-xs text-slate-400 break-all">{s}</span>)}</div>}
        <div className="pt-4 border-t border-[#2A2F3D]"><ContactActions userId={userId} initialSaved={contact.saved} onNavigate={onClose} prefix="member-card" /></div>
        <Link data-testid="member-card-profile" onClick={onClose} to={`/${contact.account_type === 'company' ? 'empresas' : 'profesionales'}/${encodeURIComponent(userId)}`} className="text-sm text-amber-500 hover:text-amber-300 transition-colors">{t('chat.profile')} →</Link>
      </>}
    </DialogContent>
  </Dialog>;
};
